import React, { useState, useCallback, useEffect } from "react";
import {
  Box,
  Flex,
  Text,
  Image,
  Button,
  Icon,
  HStack,
  Grid,
  GridItem,
  Tooltip,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  useDisclosure,
  Avatar
} from "@chakra-ui/react";
import {
  FaPhone,
  FaEnvelope,
  FaWhatsapp,
  FaHeart,
  FaBed,
  FaBath,
  FaTrash,
} from "react-icons/fa";
import { MdLocationOn, MdSquareFoot } from "react-icons/md";
import { CiHeart } from "react-icons/ci";
import { FormattedMessage, useIntl } from "react-intl";
import { useMutation } from "@tanstack/react-query";
import { Carousel } from "react-responsive-carousel";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import { useDebouncedCallback } from "use-debounce";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import ContactForm from "../PropertyDetails/ContactForm";
import { disLikeProperty, likeProperty } from "../../api/Property/propertyApi";
import { deleteLead } from "../../api/lead/leadApi";
import { setLoginModalVisible } from "../../redux/reducers/globalReducer";
import { calculateDaysAgo } from "../../utils/Helper";
import { bucket_base_url, base_url, FALLBACK_IMAGE } from "../../constants/constant";
import { generateSlug } from "../../utils/Helper";

export const PropertyCard = ({
  item,
  liked,
  refetch,
  refetchSavedProperties,
  setDislikeTrigger,
  isContacted,
  leadId,
  handleDislikeSuccess,
  isSavedProperties,
  isSavedSearches,
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { profileData } = useSelector((state) => state?.user);
  const auth = JSON.parse(localStorage.getItem("userData"));
  const toast = useToast();
  const [agent, setAgent] = useState(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const [like, setLike] = useState(liked?.length > 0 ? true : false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [disable, setDisable] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const queryClient = useQueryClient();

  const { mutate: mutateLike } = useMutation({
    mutationFn: (data) => likeProperty(data),
  });
  const { mutate: mutateDislike } = useMutation({
    mutationFn: () => disLikeProperty(liked[0]?.id),
  });

  const fetchAgentDetails = useCallback(async (userId) => {
    try {
      const response = await axios.get(`${base_url}users/${userId}`, {
        headers: { "ngrok-skip-browser-warning": true },
      });
      setAgent(response.data);
    } catch (error) {
      console.error("Failed to fetch agent details:", error);
    }
  }, []);

  useEffect(() => {
    if (item?.userId) fetchAgentDetails(item.userId);
  }, [fetchAgentDetails, item?.userId]);

  const handleWhatsAppClick = () => {
    const phoneNumber = agent?.phone;
    const message = intl.formatMessage(
      { id: "whatsapp_message" },
      {
        title: item?.title_en,
        referenceNumber: item?.reference_number,
      }
    );
    const encodedMessage = encodeURIComponent(message);
    const whatsappLink = `https://api.whatsapp.com/send?phone=${phoneNumber}&text=${encodedMessage}`;
    window.open(whatsappLink, "_blank");
  };

  const handleLike = useDebouncedCallback(() => {
    if (auth) {
      setLike((prev) => !prev);
      if (like) {
        mutateDislike("", {
          onSuccess: () => {
            refetch();
            refetchSavedProperties();
            setDislikeTrigger(true);
            handleDislikeSuccess(liked[0]?.id);
            queryClient.invalidateQueries(["PropertyLikes"]);
            toast({
              title: "Property removed from favorites.",
              status: "success",
              duration: 3000,
              isClosable: true,
              position: "top",
            });
          },
        });
      } else {
        const body = {
          propertyId: item.id,
        };
        mutateLike(body, {
          onSuccess: () => {
            refetch();
            refetchSavedProperties();
            toast({
              title: "Property added to favorites.",
              status: "success",
              duration: 3000,
              isClosable: true,
              position: "top",
            });
          },
        });
      }
    } else {
      dispatch(setLoginModalVisible(true));
    }
  }, 100);

  const { mutate: mutateDelete } = useMutation({
    mutationFn: () => deleteLead(leadId),
    onSuccess: () => {
      refetch();
      queryClient.invalidateQueries(["ContactedProperties"]);
      queryClient.invalidateQueries(["ContactedPropertiesCount"]);
      toast({
        title: "Property removed from contacted list.",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
    },
    onError: () => {
      toast({
        title: "Failed to delete contacted property.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
    },
  });

  const handleDelete = () => {
    mutateDelete();
  };

  const handleCallClick = () => {
    if (agent?.phone || "971565083186") {
      setShowTooltip(true);
      setTimeout(() => setShowTooltip(false), 3000);
    } else {
      alert("Agent contact number is not available.");
    }
  };

  console.log(agent, "agent");

  return (
    <Box
      borderWidth="1px"
      borderRadius="2xl"
      overflow="hidden"
      boxShadow="0 4px 20px rgba(0,0,0,0.05)"
      maxW={{ base: "100%", md: "1000px" }}
      m="auto"
      bg="white"
      transition="all 0.3s ease"
      _hover={{
        transform: "translateY(-4px)",
        boxShadow: "0 12px 30px rgba(0,0,0,0.1)"
      }}
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      borderColor="gray.100"
    >
      <Grid templateColumns={{ base: "1fr", md: "1.5fr 2fr" }} gap={4}>
        <GridItem>
          <Box position="relative" height="100%">
            {(!item?.file || item?.file?.length === 0) ? (
              <Box width="100%" height="100%" position="relative">
                {item?.offering_type && <Box
                  position="absolute"
                  top="2"
                  left="2"
                  bg="blackAlpha.600"
                  p="2"
                  borderRadius="md"
                  color="white"
                  zIndex="2"
                >
                  <Text fontSize="sm">{item?.offering_type}</Text>
                </Box>}
                <Box position="absolute" top="2" right="2" zIndex="2">
                  <button onClick={handleLike}>
                    {like ? (
                      <Icon as={FaHeart} color="red.500" boxSize="6" />
                    ) : (
                      <Icon as={CiHeart} color="gray.500" boxSize="6" />
                    )}
                  </button>
                </Box>
                <Image
                  src={FALLBACK_IMAGE}
                  alt="No image available"
                  objectFit="cover"
                  w="100%"
                  h="100%"
                  minH={{ base: "170px", md: "250px" }}
                  borderTopLeftRadius="2xl"
                  borderBottomLeftRadius={{ base: "0", md: "2xl" }}
                  borderTopRightRadius={{ base: "2xl", md: "0" }}
                />
              </Box>
            ) : (
              <Carousel
                showArrows
                autoPlay={false}
                infiniteLoop
                showThumbs={false}
                showIndicators={false}
                showStatus={false}
                selectedItem={currentSlide}
                onChange={setCurrentSlide}
              >
                {item?.file?.map((image, index) => (
                  <Box key={index} width="100%" height="100%">
                    {item?.offering_type && <Box
                      position="absolute"
                      top="2"
                      left="2"
                      bg="blackAlpha.600"
                      p="2"
                      borderRadius="md"
                      color="white"
                    >
                      <Text fontSize="sm">{item?.offering_type}</Text>
                    </Box>}
                    <Box position="absolute" top="2" right="2">
                      <button onClick={handleLike}>
                        {like ? (
                          <Icon as={FaHeart} color="red.500" boxSize="6" />
                        ) : (
                          <Icon as={CiHeart} color="gray.500" boxSize="6" />
                        )}
                      </button>
                    </Box>
                    <Image
                      src={
                        image?.filePath?.includes("http")
                          ? image.filePath
                          : `${bucket_base_url}${image?.filePath}`
                      }
                      alt="Property"
                      objectFit="cover"
                      width="100%"
                      height={{ base: "170px", md: "250px" }}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = FALLBACK_IMAGE;
                      }}
                    />
                  </Box>
                ))}
              </Carousel>
            )}
          </Box>
        </GridItem>

        <GridItem>
          <Box
            onClick={() =>
              navigate(
                `/property-details/${item?.id}-${generateSlug(item?.title_en)}`,
                { state: profileData }
              )
            }
            cursor="pointer"
            ml={{ base: 3, md: 0 }}
          >
            <Tooltip label={item?.title_en}>
              <Text fontSize="md" color="gray.500" mt={{ base: 0, md: 5 }}>
                {intl.formatMessage({
                  id: "apartment",
                  defaultMessage: "Apartment",
                })}
              </Text>
            </Tooltip>
            <Text fontSize="2xl" fontWeight="bold" color="gray.700" mb="2">
              {intl.formatMessage(
                { id: "aed", defaultMessage: "{price} AED" },
                { price: item?.price }
              )}
            </Text>
            <Text fontSize="lg" color="gray.600" mb="3">
              {item?.title_en}
            </Text>

            <Flex alignItems="center" mb="3">
              <Icon as={MdLocationOn} color="gray.500" mr="1" />
              <Text fontSize="md" color="gray.500">
                {item?.community}, {item?.city}
              </Text>
            </Flex>

            <Flex alignItems="center" color="gray.600" mb="4">
              <img src="/icons/bed.svg" alt="beds" style={{ width: "18px", height: "18px", opacity: 0.8 }} />
              <Text fontSize="sm" ml={2}>
                {intl.formatMessage(
                  { id: "beds", defaultMessage: "{count} Beds" },
                  { count: item?.bedroom }
                )}
              </Text>
              <Text mx="2">|</Text>
              <img src="/icons/shower.svg" alt="baths" style={{ width: "18px", height: "18px", opacity: 0.8 }} />
              <Text fontSize="sm" ml={2}>
                {item?.bathroom}{" "}
                <FormattedMessage
                  id={
                    Number(item?.bathroom) === 1
                      ? "searchBar.bath"
                      : "searchBar.baths"
                  }
                  defaultMessage={
                    Number(item?.bathroom) === 1 ? "Bath" : "Baths"
                  }
                />
              </Text>
              <Text mx="2">|</Text>
              <img src="/icons/area.svg" alt="sqft" style={{ width: "18px", height: "18px", opacity: 0.8 }} />
              <Text fontSize="sm" ml={2}>
                {intl.formatMessage(
                  { id: "sqft", defaultMessage: "{size} SQFT" },
                  { size: item?.size }
                )}
              </Text>
            </Flex>
          </Box>
        </GridItem>
      </Grid>

      <Box
        bg="gray.50"
        p={{ base: 4, md: 3 }}
        borderTop="1px solid"
        borderColor="gray.100"
        display="flex"
        flexDirection={{ base: "column", md: "row" }}
        alignItems="center"
        gap={{ base: 4, md: 0 }}
        justifyContent="space-between"
      >
        <Flex alignItems="center" w={{ base: "100%", md: "auto" }}>
          {agent && (
            // <Image
            //   borderRadius="full"
            //   boxSize="40px"
            //   src={agent.photo || "/path-to-placeholder-image.jpg"}
            //   alt="Agent"
            //   mr="2"
            // />
            <Avatar
              size="md"
              name={agent?.name}
              src={agent?.photo}
              mr="2"
              border="4px solid white"
              boxShadow="0 2px 10px rgba(0,0,0,0.1)"
              borderRadius="full"
            />
          )}
          <Text fontSize="sm" color="gray.600" ml={2}>
            {intl.formatMessage(
              {
                id: "listed_by",
                defaultMessage: "Listed {daysAgo} by {agentName}",
              },
              {
                daysAgo: item?.createdAt
                  ? calculateDaysAgo(item.createdAt)
                  : "N/A",
                agentName:
                  agent?.name ||
                  intl.formatMessage({
                    id: "agentInfo.defaultAgentName",
                    defaultMessage: "Agent",
                  }),
              }
            )}
          </Text>
        </Flex>
        <HStack
          spacing={{ base: 2, md: 4 }}
          flexWrap={{ base: "wrap", md: "nowrap" }}
          justifyContent={{ base: "flex-start", md: "flex-end" }}
          w={{ base: "100%", md: "auto" }}
        >
          <Tooltip
            isOpen={showTooltip}
            hasArrow
            bg="white"
            color="black"
            placement="top"
            p={4}
            borderRadius="md"
            boxShadow="md"
            label={
              <Box>
                <Box color="black" fontSize={15} fontWeight="bold" mb={1}>
                  This number accepts calls only
                </Box>
                <Box color="grey.600" fontSize={15}>
                  Mention the reference
                </Box>
                <Box color="red.500" fontSize={15} fontWeight="bold">
                  {`"${item?.reference_number}"`}
                </Box>
              </Box>
            }
          >
            <Button
              leftIcon={<FaPhone />}
              colorScheme="purple"
              size="sm"
              variant="outline"
              onClick={handleCallClick}
            >
              {agent?.phone || "+971565083186"}
            </Button>
          </Tooltip>
          <Button
            leftIcon={<FaEnvelope />}
            colorScheme="purple"
            size="sm"
            ml={{ base: 0, sm: 7, md: 0 }}
            variant="outline"
            onClick={onOpen}
          >
            {intl.formatMessage({
              id: "propertyCard.email",
              defaultMessage: "Email",
            })}
          </Button>
          <Button
            leftIcon={<FaWhatsapp />}
            colorScheme="purple"
            size="sm"
            variant="outline"
            onClick={handleWhatsAppClick}
          >
            {intl.formatMessage({
              id: "propertyCard.whatsapp",
              defaultMessage: "WhatsApp",
            })}
          </Button>
          <Button onClick={handleLike} colorScheme="red" variant="ghost">
            {like ? (
              <Icon as={FaHeart} color="red.500" boxSize="5" />
            ) : (
              <Icon as={CiHeart} color="gray.500" boxSize="5" />
            )}
          </Button>
          {isContacted && (
            <Button
              leftIcon={<FaTrash />}
              colorScheme="red"
              size="sm"
              variant="outline"
              onClick={handleDelete}
            >
              Delete
            </Button>
          )}
        </HStack>
      </Box>

      <Modal isOpen={isOpen} onClose={onClose} size="md" zIndex={1500}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {intl.formatMessage(
              {
                id: "propertyCard.contactAgent",
                defaultMessage: "Contact {agentName}",
              },
              {
                agentName:
                  agent?.name ||
                  intl.formatMessage({
                    id: "agentInfo.defaultAgentName",
                    defaultMessage: "Agent",
                  }),
              }
            )}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <ContactForm
              agent={agent}
              auth={auth}
              property={item}
              onClose={onClose}
              setDisable={setDisable}
              disable={disable}
            />
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};
